(() => {
  class LicohennaProductGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-product-main-image]');
      this.thumbs = Array.from(this.querySelectorAll('[data-product-thumb]'));
      if (!this.main || this.thumbs.length === 0) return;

      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const thumb = event.target.closest('[data-product-thumb]');
      if (!thumb || !this.contains(thumb)) return;

      event.preventDefault();
      this.main.src = thumb.getAttribute('data-image-src') || this.main.src;
      this.main.srcset = thumb.getAttribute('data-image-srcset') || '';
      this.main.alt = thumb.getAttribute('data-image-alt') || this.main.alt;

      this.thumbs.forEach((item) => {
        item.setAttribute('aria-current', item === thumb ? 'true' : 'false');
      });
    }
  }

  if (!customElements.get('licohenna-product-gallery')) {
    customElements.define('licohenna-product-gallery', LicohennaProductGallery);
  }

  const applyAtcLabel = (root) => {
    root.querySelectorAll('[data-atc-label]').forEach((actions) => {
      const label = actions.getAttribute('data-atc-label');
      const button = actions.querySelector('buy-buttons button[type="submit"]');
      if (!label || !button || button.disabled) return;
      button.textContent = label;
    });
  };

  const isMobileUsageLayout = () => window.matchMedia('(max-width: 699px)').matches;

  const layoutUsageBlock = (usage) => {
    const media = usage.querySelector('.licohenna-product__usage-media');
    const text = usage.querySelector('.licohenna-product__usage-text');
    if (!media || !text) return;

    // 旧 margin-top 方式の残骸を必ずクリア
    media.style.marginTop = '';

    if (isMobileUsageLayout()) {
      usage.style.removeProperty('--usage-spacer-height');
      return;
    }

    // 1) spacer=0・画像をフロー外にして、本文を全文幅で計測
    usage.style.setProperty('--usage-spacer-height', '0px');
    media.style.float = 'none';
    media.style.clear = 'none';
    media.style.position = 'absolute';
    media.style.visibility = 'hidden';
    media.style.pointerEvents = 'none';

    const textHeight = text.getBoundingClientRect().height;
    const imageHeight = media.getBoundingClientRect().height;

    media.style.position = '';
    media.style.visibility = '';
    media.style.pointerEvents = '';
    media.style.float = '';
    media.style.clear = '';

    // 2) 透明 spacer 高さ = 本文高さ - 画像高さ + 上余白(10px)
    const spacerGap = 10;
    let spacerHeight = Math.max(0, Math.round(textHeight - imageHeight + spacerGap));
    usage.style.setProperty('--usage-spacer-height', `${spacerHeight}px`);

    // 3) 回り込み後の高さ変化に合わせて数回収束（余白は初回計算のみ加算）
    for (let i = 0; i < 3; i += 1) {
      void usage.offsetHeight;
      const blockHeight = usage.getBoundingClientRect().height;
      const mediaHeight = media.getBoundingClientRect().height;
      const next = Math.max(0, Math.round(blockHeight - mediaHeight));
      if (Math.abs(next - spacerHeight) <= 1) break;
      spacerHeight = next;
      usage.style.setProperty('--usage-spacer-height', `${spacerHeight}px`);
    }
  };

  const layoutUsageImages = (root) => {
    if (root instanceof Element && root.classList.contains('licohenna-product__usage--with-image')) {
      layoutUsageBlock(root);
      return;
    }

    const scope = root instanceof Element ? root : document;
    scope.querySelectorAll('.licohenna-product__usage--with-image').forEach(layoutUsageBlock);
  };

  let usageLayoutFrame = 0;
  const scheduleUsageLayout = (root) => {
    if (usageLayoutFrame) window.cancelAnimationFrame(usageLayoutFrame);
    usageLayoutFrame = window.requestAnimationFrame(() => {
      usageLayoutFrame = 0;
      layoutUsageImages(root);
    });
  };

  const bindUsageLayout = (usage) => {
    if (usage.dataset.usageLayoutBound === 'true') return;
    usage.dataset.usageLayoutBound = 'true';

    const image = usage.querySelector('.licohenna-product__usage-image');
    if (image && !image.complete) {
      image.addEventListener('load', () => scheduleUsageLayout(usage), { once: true });
    }

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => scheduleUsageLayout(usage));
      observer.observe(usage);
      const text = usage.querySelector('.licohenna-product__usage-text');
      if (text) observer.observe(text);
    }
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const bindAccordion = (details) => {
    if (details.dataset.accordionBound === 'true') return;

    const summary = details.querySelector('summary');
    const content = details.querySelector('.licohenna-product__accordion-content');
    const inner = details.querySelector('.licohenna-product__accordion-inner');
    if (!summary || !content || !inner) return;

    details.dataset.accordionBound = 'true';

    const duration = 320;
    let animating = false;
    let targetOpen = details.open;
    let onTransitionEnd = null;
    let fallbackTimer = 0;
    let frameId = 0;

    const clearAnimation = () => {
      if (onTransitionEnd) {
        content.removeEventListener('transitionend', onTransitionEnd);
        onTransitionEnd = null;
      }
      if (fallbackTimer) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = 0;
      }
      if (frameId) {
        window.cancelAnimationFrame(frameId);
        frameId = 0;
      }
    };

    const currentHeight = () => content.getBoundingClientRect().height;

    const targetHeight = () => inner.getBoundingClientRect().height;

    const finish = (open) => {
      clearAnimation();
      details.classList.remove('is-animating');
      details.open = open;
      content.style.height = '';
      content.style.opacity = '';
      targetOpen = open;
      animating = false;
      if (open) scheduleUsageLayout(details);
    };

    const animateHeight = (from, to, open) => {
      clearAnimation();
      animating = true;
      targetOpen = open;
      details.classList.remove('is-animating');
      content.style.height = `${Math.max(from, 0)}px`;
      content.style.opacity = from <= 0 ? '0' : '1';
      void content.offsetHeight;

      if (Math.abs(from - to) < 0.5) {
        finish(open);
        return;
      }

      onTransitionEnd = (event) => {
        if (event.target !== content || event.propertyName !== 'height') return;
        finish(open);
      };
      content.addEventListener('transitionend', onTransitionEnd);

      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        details.classList.add('is-animating');
        content.style.height = `${Math.max(to, 0)}px`;
        content.style.opacity = to <= 0 ? '0' : '1';
      });

      fallbackTimer = window.setTimeout(() => {
        if (!animating || targetOpen !== open) return;
        finish(open);
      }, duration + 80);
    };

    const openAccordion = () => {
      const from = details.open ? currentHeight() : 0;
      details.open = true;
      content.style.height = `${Math.max(from, 0)}px`;
      content.style.opacity = from <= 0 ? '0' : String(content.style.opacity || '1');
      void content.offsetHeight;
      animateHeight(from, targetHeight(), true);
    };

    const closeAccordion = () => {
      const from = currentHeight() || targetHeight();
      animateHeight(from, 0, false);
    };

    summary.addEventListener('click', (event) => {
      if (prefersReducedMotion()) return;

      event.preventDefault();

      if (animating) {
        if (targetOpen) {
          closeAccordion();
        } else {
          openAccordion();
        }
        return;
      }

      if (targetOpen) {
        closeAccordion();
        return;
      }

      openAccordion();
    });
  };

  const enhanceRoot = (root) => {
    if (!(root instanceof Element)) return;
    applyAtcLabel(root);
    root.querySelectorAll('.licohenna-product__accordion').forEach(bindAccordion);
    root.querySelectorAll('.licohenna-product__usage--with-image').forEach(bindUsageLayout);
    scheduleUsageLayout(root);
  };

  const enhanceAll = () => {
    document.querySelectorAll('.shopify-section--licohenna-product').forEach(enhanceRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhanceAll, { once: true });
  } else {
    enhanceAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root =
      event.target?.closest?.('.shopify-section--licohenna-product') || event.target;
    if (root?.querySelector?.('.licohenna-product__accordion, .licohenna-product__usage--with-image')) {
      enhanceRoot(root);
    } else {
      enhanceAll();
    }
  });

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      document.querySelectorAll('.shopify-section--licohenna-product').forEach((section) => {
        scheduleUsageLayout(section);
      });
    }, 100);
  });

})();
